import { DEV } from 'cc/env';
import { _decorator, Component, Node, TransformBit, Enum, Vec3, director, Director, v3, Size, Rect, Quat } from 'cc';
import Delay from '../../Utils/Delay';

const { ccclass, property, executeInEditMode } = _decorator;
const targetWorldPosition = v3();
const targetWorldEulerAngles = v3();
const targetWorldScale = v3();

@ccclass('BindNode')
@executeInEditMode
export default class BindNode extends Component {
    @property(Node)
    private _target: Node = null;

    @property(Node)
    private _additionalTarget: Node = null;

    @property({ type: Enum(TransformBit) })
    private _bindFlag: TransformBit = Node.TransformBit.NONE;

    @property
    private _keepPosition: boolean = false;

    @property
    private _keepScale: boolean = false;

    @property
    private _keepRotation: boolean = false;

    @property
    private _onlyUpdateX: boolean = false;

    @property
    private _onlyUpdateY: boolean = false;

    @property
    private _positionOffset: Vec3 = Vec3.ZERO.clone();

    @property({ formerlySerializedAs: 'applyPositionBoundary' })
    private _applyPositionBoundary: boolean = false;

    @property
    private _positionBoundary: Rect = new Rect();

    @property
    private _rotationOffset: Vec3 = Vec3.ZERO.clone();

    @property
    private _scaleOffset: Vec3 = Vec3.ONE.clone();

    @property({ formerlySerializedAs: '_adjustContentSize' })
    private _bindContentSize: boolean = false;

    @property
    private _delayOneFrameUpdateSize: boolean = false;

    @property
    private _isBindWorldBoundingBox: boolean = false;

    @property
    private _additionSize: Size = Size.ZERO.clone();

    @property
    private _scalarSize: Size = Size.ONE.clone();

    @property({ formerlySerializedAs: '_adjustAnchor' })
    private _bindAnchor: boolean = false;

    @property
    private _bindActive: boolean = false;

    @property({ formerlySerializedAs: '_adjustOpacity' })
    private _bindOpacity: boolean = false;

    private _dirtyTransformBit: TransformBit = TransformBit.NONE;
    private _dirtySize: boolean = false;
    private _originalPosition: Vec3 = Vec3.ZERO.clone();
    private _originalScale: Vec3 = Vec3.ONE.clone();
    private _originalRotation: Quat = Quat.IDENTITY.clone();

    constructor(name?: string) {
        super(name);
        // eslint-disable-next-line no-constructor-return
        return new Proxy(this, {
            set: (target, p, value) => {
                // to ensure active is true to register event listener correctly
                if (p === '_bindActive') {
                    const bindActive = value as boolean;
                    if (bindActive && target.node) {
                        target.node.active = true;
                    }
                } else if (p === 'node') {
                    const node = value as Node;
                    if (node && target._bindActive) {
                        node.active = true;
                    }
                }
                return Reflect.set(target, p, value);
            },
        });
    }

    @property(Node)
    public get target() {
        return this._target;
    }
    public set target(value: Node) {
        if (this._target === value) {
            return;
        }
        if (this._target) {
            this._unregisterTargetEvent(this._target);
        }
        this._target = value;
        this._applyTarget(value);
    }

    @property(Node)
    public get additionalTarget() {
        return this._additionalTarget;
    }
    public set additionalTarget(value: Node) {
        if (this._additionalTarget === value) {
            return;
        }
        if (this._additionalTarget) {
            this._unregisterTargetEvent(this._additionalTarget);
        }
        this._additionalTarget = value;
        this._applyTarget(value);
    }

    @property({ type: Enum(TransformBit), group: 'TRS' })
    public get bindFlag(): TransformBit {
        return this._bindFlag;
    }
    public set bindFlag(value: TransformBit) {
        if (this._bindFlag === value) {
            return;
        }
        this._bindFlag = value;
        this._onTargetTransformChanged(value);
    }

    @property({
        group: 'TRS',
        visible() {
            return !!((this._bindFlag as number) & Node.TransformBit.POSITION);
        },
        tooltip: DEV && '啟用時確保維持初始位置',
    })
    public get keepPosition() {
        return this._keepPosition;
    }
    public set keepPosition(value: boolean) {
        if (this._keepPosition === value) {
            return;
        }
        this._keepPosition = value;
        this._onTargetTransformChanged(Node.TransformBit.POSITION);
    }

    @property({
        visible(this: BindNode) {
            return !!((this._bindFlag as number) & Node.TransformBit.POSITION) && !this.keepPosition && !this._onlyUpdateY;
        },
        group: 'TRS',
    })
    public get onlyUpdateX() {
        return this._onlyUpdateX;
    }
    public set onlyUpdateX(value: boolean) {
        if (this._onlyUpdateX === value) {
            return;
        }
        this._onlyUpdateX = value;
        this._onTargetTransformChanged(Node.TransformBit.POSITION);
    }

    @property({
        visible(this: BindNode) {
            return !!((this._bindFlag as number) & Node.TransformBit.POSITION) && !this.keepPosition && !this._onlyUpdateX;
        },
        group: 'TRS',
    })
    public get onlyUpdateY() {
        return this._onlyUpdateY;
    }
    public set onlyUpdateY(value: boolean) {
        if (this._onlyUpdateY === value) {
            return;
        }
        this._onlyUpdateY = value;
        this._onTargetTransformChanged(Node.TransformBit.POSITION);
    }

    @property({
        visible(this: BindNode) {
            return !!((this._bindFlag as number) & Node.TransformBit.POSITION) && !this.keepPosition;
        },
        group: 'TRS',
    })
    public get positionOffset() {
        return this._positionOffset;
    }
    public set positionOffset(value: Vec3) {
        if (this._positionOffset.equals(value)) {
            return;
        }
        this._positionOffset = value;
        this._onTargetTransformChanged(Node.TransformBit.POSITION);
    }

    @property({
        visible(this: BindNode) {
            return !!((this._bindFlag as number) & Node.TransformBit.POSITION) && !this.keepPosition;
        },
        group: 'TRS',
    })
    public get applyPositionBoundary() {
        return this._applyPositionBoundary;
    }
    public set applyPositionBoundary(value: boolean) {
        if (this._applyPositionBoundary === value) {
            return;
        }
        this._applyPositionBoundary = value;
        this._onTargetTransformChanged(Node.TransformBit.POSITION);
    }

    @property({
        visible(this: BindNode) {
            return !!((this._bindFlag as number) & Node.TransformBit.POSITION) && this._applyPositionBoundary && !this.keepPosition;
        },
        group: 'TRS',
    })
    public get positionBound() {
        return this._positionBoundary;
    }
    public set positionBound(value: Rect) {
        if (this._positionBoundary === value) {
            return;
        }
        this._positionBoundary = value;
        this._onTargetTransformChanged(Node.TransformBit.POSITION);
    }

    @property({
        group: 'TRS',
        visible() {
            return !!((this._bindFlag as number) & Node.TransformBit.ROTATION);
        },
        tooltip: DEV && '啟用時確保維持初始旋轉',
    })
    public get keepRotation() {
        return this._keepRotation;
    }
    public set keepRotation(value: boolean) {
        if (this._keepRotation === value) {
            return;
        }
        this._keepRotation = value;
        this._onTargetTransformChanged(Node.TransformBit.ROTATION);
    }

    @property({
        visible(this: BindNode) {
            return !!((this._bindFlag as number) & Node.TransformBit.ROTATION) && !this.keepRotation;
        },
        group: 'TRS',
    })
    public get rotationOffset() {
        return this._rotationOffset;
    }
    public set rotationOffset(value: Vec3) {
        if (this._rotationOffset.equals(value)) {
            return;
        }
        this._rotationOffset = value;
        this._onTargetTransformChanged(Node.TransformBit.ROTATION);
    }

    @property({
        group: 'TRS',
        visible() {
            return !!((this._bindFlag as number) & Node.TransformBit.SCALE);
        },
        tooltip: DEV && '啟用時確保維持初始縮放',
    })
    public get keepScale() {
        return this._keepScale;
    }
    public set keepScale(value: boolean) {
        if (this._keepScale === value) {
            return;
        }
        this._keepScale = value;
        this._onTargetTransformChanged(Node.TransformBit.SCALE);
    }

    @property({
        visible(this: BindNode) {
            return !!((this._bindFlag as number) & Node.TransformBit.SCALE) && !this.keepScale;
        },
        group: 'TRS',
        tooltip: DEV && 'The scale offset which will be multiplied to the target scale',
    })
    public get scaleOffset() {
        return this._scaleOffset;
    }
    public set scaleOffset(value: Vec3) {
        if (this._scaleOffset.equals(value)) {
            return;
        }
        this._scaleOffset = value;
        this._onTargetTransformChanged(Node.TransformBit.SCALE);
    }

    @property({ formerlySerializedAs: 'adjustContentSize', group: 'ContentSize' })
    public get bindContentSize() {
        return this._bindContentSize;
    }
    public set bindContentSize(value: boolean) {
        if (this._bindContentSize === value) {
            return;
        }
        this._bindContentSize = value;
        this._onTargetSizeChanged();
    }

    @property({ group: 'ContentSize', tooltip: DEV && '啟用後會在下一幀更新尺寸，可能會導致一幀的尺寸不正確，但可以避免某些情況下更新不生效(如:bind label時)' })
    public get delayOneFrameUpdateSize() {
        return this._delayOneFrameUpdateSize;
    }
    public set delayOneFrameUpdateSize(value: boolean) {
        if (this._delayOneFrameUpdateSize === value) {
            return;
        }
        this._delayOneFrameUpdateSize = value;
    }

    @property({
        visible(this: BindNode) {
            return this.bindContentSize;
        },
        group: 'ContentSize',
    })
    public get isBindWorldBoundingBox() {
        return this._isBindWorldBoundingBox;
    }
    public set isBindWorldBoundingBox(value: boolean) {
        if (this._isBindWorldBoundingBox === value) {
            return;
        }
        this._isBindWorldBoundingBox = value;
        this._onTargetSizeChanged();
    }

    @property({
        visible(this: BindNode) {
            return this.bindContentSize;
        },
        group: 'ContentSize',
    })
    public get additionSize() {
        return this._additionSize;
    }
    public set additionSize(value: Size) {
        if (this._additionSize.equals(value)) {
            return;
        }
        this._additionSize = value;
        this._onTargetSizeChanged();
    }

    @property({
        visible(this: BindNode) {
            return this.bindContentSize;
        },
        group: 'ContentSize',
    })
    public get scalarSize() {
        return this._scalarSize;
    }
    public set scalarSize(value: Size) {
        if (this._scalarSize.equals(value)) {
            return;
        }
        this._scalarSize = value;
        this._onTargetSizeChanged();
    }

    @property({ formerlySerializedAs: 'adjustAnchor', group: 'Misc' })
    public get bindAnchor() {
        return this._bindAnchor;
    }
    public set bindAnchor(value: boolean) {
        if (this._bindAnchor === value) {
            return;
        }
        this._bindAnchor = value;
        this._onTargetAnchorChanged();
    }

    @property({ group: 'Misc' })
    public get bindOpacity() {
        return this._bindOpacity;
    }
    public set bindOpacity(value: boolean) {
        if (this._bindOpacity === value) {
            return;
        }
        this._bindOpacity = value;
    }

    @property({ group: 'Misc' })
    public get bindActive() {
        return this._bindActive;
    }
    public set bindActive(value: boolean) {
        if (this._bindActive === value) {
            return;
        }
        this._bindActive = value;
        this._onTargetActiveChanged();
    }

    public forceUpdate() {
        this._onTargetTransformChanged(Node.TransformBit.TRS);
        this._onTargetSizeChanged();
        this._onTargetAnchorChanged();
        this._onTargetActiveChanged();
    }

    // eslint-disable-next-line @typescript-eslint/naming-convention
    protected __preload() {
        this._applyTarget(this.target);
        this._applyTarget(this.additionalTarget);
    }

    protected onEnable() {
        director.on(Director.EVENT_AFTER_UPDATE, this._syncWithTarget, this);
        this.node.on(Node.EventType.SIZE_CHANGED, this._onSizeChanged, this);
        this._syncWithTarget(true);
    }

    protected onDisable() {
        director.off(Director.EVENT_AFTER_UPDATE, this._syncWithTarget, this);
        this.node.off(Node.EventType.SIZE_CHANGED, this._onSizeChanged, this);
    }

    protected update() {
        if (this._bindOpacity && this.target) {
            this.node.opacity = this.target.opacity;
        }
    }

    private _applyTarget(target: Node) {
        if (!target) {
            return;
        }
        // record origin transform
        this.node.getWorldPosition(this._originalPosition);
        this.node.getWorldScale(this._originalScale);
        this.node.getWorldRotation(this._originalRotation);
        this._registerTargetEvent(target);
        this._onTargetTransformChanged(Node.TransformBit.TRS);
        this._onTargetSizeChanged();
        this._onTargetAnchorChanged();
        this._onTargetActiveChanged();
    }

    private _registerTargetEvent(target: Node) {
        target.on(Node.EventType.TRANSFORM_CHANGED, this._onTargetTransformChanged, this);
        target.on(Node.EventType.SIZE_CHANGED, this._onTargetSizeChanged, this);
        if (this.isBindWorldBoundingBox) {
            // worldBoundingBox is affected by scale
            target.on(Node.EventType.TRANSFORM_CHANGED, this._onTargetSizeChanged, this);
        }
        target.on(Node.EventType.ANCHOR_CHANGED, this._onTargetAnchorChanged, this);
        target.on(Node.EventType.ACTIVE_CHANGED, this._onTargetActiveChanged, this);
    }

    private _unregisterTargetEvent(target: Node) {
        target.off(Node.EventType.TRANSFORM_CHANGED, this._onTargetTransformChanged, this);
        target.off(Node.EventType.SIZE_CHANGED, this._onTargetSizeChanged, this);
        if (this.isBindWorldBoundingBox) {
            target.off(Node.EventType.TRANSFORM_CHANGED, this._onTargetSizeChanged, this);
        }
        target.off(Node.EventType.ANCHOR_CHANGED, this._onTargetAnchorChanged, this);
        target.off(Node.EventType.ACTIVE_CHANGED, this._onTargetActiveChanged, this);
    }

    private _onTargetTransformChanged(type: TransformBit) {
        const temp: number = (this._dirtyTransformBit as number) | ((type as number) & (this.bindFlag as number));
        this._dirtyTransformBit = temp;
    }

    private async _onTargetSizeChanged() {
        if (this.bindContentSize) {
            if (this.delayOneFrameUpdateSize) {
                await Delay.resolve();
            }
            if (this.isBindWorldBoundingBox) {
                const size = this.target?.transform?.getWorldBoundingBox().size.clone();
                size.width += this.additionSize.width;
                size.height += this.additionSize.height;
                size.width *= this._scalarSize.width;
                size.height *= this._scalarSize.height;
                this.node.transform.contentSize = size;
            } else {
                const size = this.target?.transform?.contentSize.clone();
                size.width += this.additionSize.width;
                size.height += this.additionSize.height;
                size.width *= this._scalarSize.width;
                size.height *= this._scalarSize.height;
                this.node.transform.contentSize = size;
            }
        }
    }

    private _onTargetAnchorChanged() {
        if (this.bindAnchor) {
            this.node.transform.setAnchorPoint(this.target.transform.anchorPoint);
        }
    }

    private _onTargetActiveChanged() {
        if (this.bindActive) {
            this.node.active = this.target.active;
        }
    }

    private _onSizeChanged() {
        this._dirtySize = true;
    }

    private _syncWithTarget(force = false) {
        if (!this.target) {
            return;
        }
        if (!force && !this._dirtyTransformBit && !this._dirtySize) {
            return;
        }
        const bindFlag: number = this.bindFlag as number;
        const dirtyTransformBit: number = this._dirtyTransformBit as number;
        const updatePositionBoundary = this._applyPositionBoundary && (force || this._dirtySize);
        if ((bindFlag & Node.TransformBit.POSITION && (force || dirtyTransformBit)) || updatePositionBoundary) {
            // always update position when transform dirty if keepPosition is true
            if (this.keepPosition) {
                this.node.setWorldPosition(this._originalPosition);
            } else if (dirtyTransformBit & Node.TransformBit.POSITION || updatePositionBoundary) {
                this.target.getWorldPosition(targetWorldPosition);
                if (this.onlyUpdateX) {
                    targetWorldPosition.y = this._originalPosition.y;
                } else if (this.onlyUpdateY) {
                    targetWorldPosition.x = this._originalPosition.x;
                }
                targetWorldPosition.add(this._positionOffset);
                this.node.worldPosition = targetWorldPosition;
                this._checkPositionBoundary();
            }
        }
        if (bindFlag & Node.TransformBit.ROTATION && (force || dirtyTransformBit & Node.TransformBit.ROTATION)) {
            if (this.keepRotation) {
                this.node.setWorldRotation(this._originalRotation);
            } else {
                this.target.worldRotation.getEulerAngles(targetWorldEulerAngles);
                targetWorldEulerAngles.add(this._rotationOffset);
                const { x, y, z } = targetWorldEulerAngles;
                this.node.setWorldRotationFromEuler(x, y, z);
            }
        }
        if (bindFlag & Node.TransformBit.SCALE && (force || dirtyTransformBit & Node.TransformBit.SCALE)) {
            if (this.keepScale) {
                this.node.setWorldScale(this._originalScale);
            } else {
                this.node.setWorldScale(this.target.getWorldScale(targetWorldScale).multiply(this._scaleOffset));
            }
        }
        this._dirtyTransformBit = TransformBit.NONE;
        this._dirtySize = false;
    }

    private _checkPositionBoundary() {
        if (!this._applyPositionBoundary) {
            return;
        }
        const currentRect = this.node.transform.getBoundingBox();
        let xOffset = 0;
        let yOffset = 0;
        if (currentRect.xMin < this.positionBound.xMin) {
            xOffset = this.positionBound.xMin - currentRect.xMin;
        }
        if (currentRect.xMax > this.positionBound.xMax) {
            xOffset = this.positionBound.xMax - currentRect.xMax;
        }
        if (currentRect.yMin < this.positionBound.yMin) {
            yOffset = this.positionBound.yMin - currentRect.yMin;
        }
        if (currentRect.yMax > this.positionBound.yMax) {
            yOffset = this.positionBound.yMax - currentRect.yMax;
        }
        this.node.position.add3f(xOffset, yOffset, 0);
    }
}
